import type { Color, EngineScore, Theme } from '../../types';
import { scoreFromPlayerPov } from '../engine/uci';
import { computeReplyFacts, PIECE_VALUES } from './punishment';

/**
 * Détection de thèmes pédagogiques. Chaque thème n'est attribué que s'il
 * est vérifiable mécaniquement à partir de la position et de la ligne
 * moteur — jamais déduit d'une supposition sur l'intention du joueur.
 * S'appuie sur `computeReplyFacts` (même calcul que la « punition » affichée
 * à l'utilisateur — une seule source de vérité pour les deux).
 */

export interface ThemeContext {
  fenAfterPlayed: string; // position après le coup du joueur
  opponentBestPvUci: string[] | undefined; // meilleure ligne adverse depuis fenAfterPlayed
  evalBeforePlayer: EngineScore; // POV Blancs, avant le coup du joueur
  evalAfterPlayedPlayer: EngineScore; // POV Blancs, après le coup du joueur
  playerColor: Color;
  lossCp: number;
}

export function detectThemes(ctx: ThemeContext): Theme[] {
  const themes = new Set<Theme>();

  const evalBeforePov = scoreFromPlayerPov(ctx.evalBeforePlayer, ctx.playerColor);
  const evalAfterPov = scoreFromPlayerPov(ctx.evalAfterPlayedPlayer, ctx.playerColor);

  // --- Mat manqué : un mat était disponible avant le coup, et ne l'est plus après.
  if (evalBeforePov.type === 'mate' && evalBeforePov.value > 0) {
    if (!(evalAfterPov.type === 'mate' && evalAfterPov.value > 0)) {
      themes.add('mat_manqué');
    }
  }

  // --- Mat évitable : le coup joué autorise un mat forcé contre le joueur.
  if (evalAfterPov.type === 'mate' && evalAfterPov.value < 0) {
    themes.add('mat_évitable');
  }

  const facts = computeReplyFacts(ctx.fenAfterPlayed, ctx.opponentBestPvUci, ctx.playerColor);
  if (facts) {
    // --- Pièce en prise : la meilleure réponse adverse est une capture
    // significative (>= mineure), cohérente avec la perte mesurée.
    if (facts.captured) {
      const capturedValue = PIECE_VALUES[facts.captured.piece] ?? 0;
      if (capturedValue >= 300 && ctx.lossCp >= 200) {
        themes.add('pièce_en_prise');
      }
    }
    // --- Sécurité du roi : cette réponse met le joueur en échec.
    if (!themes.has('mat_évitable') && facts.givesCheck) {
      themes.add('sécurité_du_roi');
    }
    // --- Fourchette : la pièce qui vient de jouer attaque au moins deux
    // pièces du joueur de valeur >= mineure.
    if (facts.forkedSquares.length >= 2) {
      themes.add('fourchette');
    }
  }

  // --- Repli générique, uniquement si aucun thème spécifique n'a été
  // trouvé mais que la perte est significative (matérielle par construction
  // de la mesure d'évaluation).
  if (themes.size === 0) {
    if (ctx.lossCp >= 200) themes.add('perte_matérielle');
    else if (ctx.lossCp >= 50) themes.add('coup_plus_précis_disponible');
  }

  return [...themes];
}
